/**
 * Admin Users — relationship tree + App GPS pin + consent roster (CONV-0).
 * Visibility from GET /locations/latest + GET /locations/consents.
 */

/**
 * @param {object[]} items
 * @returns {Map<string, object>}
 */
export function indexPhoneLocationsByUser(items) {
  const out = new Map();
  for (const raw of Array.isArray(items) ? items : []) {
    const u = String((raw && (raw.username || raw.user_id)) || "")
      .trim()
      .toLowerCase();
    if (!u) continue;
    out.set(u, raw);
  }
  return out;
}

/**
 * @param {object[]} items
 * @returns {Map<string, object>}
 */
export function indexConsentsByUser(items) {
  const out = new Map();
  for (const raw of Array.isArray(items) ? items : []) {
    const u = String((raw && (raw.username || raw.user_id)) || "")
      .trim()
      .toLowerCase();
    if (!u) continue;
    out.set(u, raw);
  }
  return out;
}

/**
 * @param {object|null|undefined} loc
 * @returns {string}
 */
export function formatAccuracyM(loc) {
  if (!loc || typeof loc !== "object") return "";
  const n = Number(loc.accuracy_m);
  if (!Number.isFinite(n) || n < 0) return "";
  if (n < 10) return `±${Math.round(n * 10) / 10} m`;
  return `±${Math.round(n)} m`;
}

/**
 * @param {object|null|undefined} consent
 * @returns {{ kind: "active"|"revoked"|"never", label: string }}
 */
export function consentRosterStatus(consent) {
  if (!consent || typeof consent !== "object") {
    return { kind: "never", label: "No consent" };
  }
  const st = String(consent.status || "").toLowerCase();
  const active =
    consent.active === true ||
    consent.active === 1 ||
    consent.active === "1" ||
    (st === "active" && !consent.revoked_at);
  if (active && !consent.revoked_at) {
    const ver = String(consent.consent_version || "").trim();
    return { kind: "active", label: ver ? `Consent · ${ver}` : "Consent · active" };
  }
  return { kind: "revoked", label: "Consent revoked" };
}

/**
 * @param {object} loc
 * @param {(v: unknown) => string} fmtRel
 * @returns {{ kind: "fresh"|"stale"|"none", label: string }}
 */
export function phonePinStatus(loc, fmtRel) {
  if (!loc || typeof loc !== "object") {
    return { kind: "none", label: "No pin" };
  }
  const stale = !!(
    loc.is_stale === true ||
    loc.is_stale === 1 ||
    loc.is_stale === "1"
  );
  const whenRaw = loc.recorded_at != null ? loc.recorded_at : loc.ts;
  let when = "";
  try {
    when = whenRaw != null && whenRaw !== "" && typeof fmtRel === "function" ? fmtRel(whenRaw) : "";
  } catch (_) {
    when = "";
  }
  if (when === "—" || when === "\u2014") when = "";
  const acc = formatAccuracyM(loc);
  const accBit = acc ? ` · ${acc}` : "";
  if (stale) {
    const reason = String(loc.stale_reason || "outdated").replace(/_/g, " ");
    return {
      kind: "stale",
      label: when ? `Stale · ${when}${accBit}` : `Stale · ${reason}${accBit}`,
    };
  }
  return { kind: "fresh", label: when ? `Fresh · ${when}${accBit}` : `Fresh${accBit}` };
}

/**
 * @param {object[]} users
 * @returns {{ roots: object[], childrenOf: Map<string, object[]> }}
 */
export function buildManagerForest(users) {
  const list = Array.isArray(users) ? users.slice() : [];
  const byLower = new Map();
  for (const u of list) {
    const name = String((u && u.username) || "").trim();
    if (name) byLower.set(name.toLowerCase(), u);
  }
  /** @type {Map<string, object[]>} */
  const childrenOf = new Map();
  /** @type {object[]} */
  const roots = [];
  for (const u of list) {
    const name = String((u && u.username) || "").trim();
    if (!name) continue;
    const mgr = String((u && u.manager_admin) || "").trim();
    if (mgr && byLower.has(mgr.toLowerCase()) && mgr.toLowerCase() !== name.toLowerCase()) {
      const key = mgr.toLowerCase();
      if (!childrenOf.has(key)) childrenOf.set(key, []);
      childrenOf.get(key).push(u);
    } else {
      roots.push(u);
    }
  }
  const roleRank = (u) => {
    const r = String((u && u.role) || "").toLowerCase();
    if (r === "superadmin") return 0;
    if (r === "admin") return 1;
    return 2;
  };
  const sortUsers = (arr) =>
    arr.sort((a, b) => {
      const d = roleRank(a) - roleRank(b);
      if (d) return d;
      return String(a.username || "").localeCompare(String(b.username || ""));
    });
  sortUsers(roots);
  for (const [, kids] of childrenOf) sortUsers(kids);
  return { roots, childrenOf };
}

/**
 * @param {object[]} users
 * @param {object[]} locationItems
 * @param {{ escapeHtml: Function, fmtRel: Function, consentItems?: object[] }} helpers
 */
export function renderTeamLocationTreeHtml(users, locationItems, helpers) {
  const escapeHtml = helpers && helpers.escapeHtml ? helpers.escapeHtml : (s) => String(s || "");
  const fmtRel = helpers && helpers.fmtRel ? helpers.fmtRel : () => "";
  const consentItems = helpers && Array.isArray(helpers.consentItems) ? helpers.consentItems : [];
  const list = Array.isArray(users) ? users : [];
  if (!list.length) {
    return `<p class="muted settings-admin-empty">No users in directory yet.</p>`;
  }
  const locBy = indexPhoneLocationsByUser(locationItems);
  const consentBy = indexConsentsByUser(consentItems);
  const { roots, childrenOf } = buildManagerForest(list);
  const rowBadges = (u) => {
    const key = String((u && u.username) || "")
      .trim()
      .toLowerCase();
    const st = phonePinStatus(locBy.get(key), fmtRel);
    const cs = consentRosterStatus(consentBy.get(key));
    const pinCls =
      st.kind === "fresh"
        ? "badge online"
        : st.kind === "stale"
          ? "badge revoked"
          : "muted";
    const consCls =
      cs.kind === "active"
        ? "badge online"
        : cs.kind === "revoked"
          ? "badge revoked"
          : "muted";
    return `<span class="${consCls}" style="font-size:11px">${escapeHtml(cs.label)}</span>
        <span class="${pinCls}" style="font-size:11px">${escapeHtml(st.label)}</span>`;
  };
  const renderNode = (u, depth) => {
    const name = String((u && u.username) || "").trim();
    const role = String((u && u.role) || "").trim();
    const kids = childrenOf.get(name.toLowerCase()) || [];
    const pad = Math.min(depth, 4) * 14;
    const kidsHtml = kids.map((k) => renderNode(k, depth + 1)).join("");
    return `<li class="team-loc-tree__node" style="padding-left:${pad}px">
      <div class="team-loc-tree__row" style="display:flex;flex-wrap:wrap;gap:6px 10px;align-items:center">
        <strong>${escapeHtml(name)}</strong>
        <span class="mono muted" style="font-size:11px">${escapeHtml(role || "—")}</span>
        ${rowBadges(u)}
      </div>
      ${kidsHtml ? `<ul class="team-loc-tree__kids">${kidsHtml}</ul>` : ""}
    </li>`;
  };
  const body = roots.map((r) => renderNode(r, 0)).join("");
  const withPin = list.filter((u) => {
    const k = String((u && u.username) || "")
      .trim()
      .toLowerCase();
    return locBy.has(k);
  }).length;
  const withConsent = list.filter((u) => {
    const k = String((u && u.username) || "")
      .trim()
      .toLowerCase();
    return consentRosterStatus(consentBy.get(k)).kind === "active";
  }).length;
  return `<p class="muted" style="margin:0 0 10px;font-size:12px;line-height:1.4">
      ${escapeHtml(String(withConsent))} consented · ${escapeHtml(String(withPin))} of ${escapeHtml(String(list.length))} have a visible App pin
      (same scope as Home). Accuracy shown when the App reports it. Map: <button type="button" class="btn sm ghost btn-tap" data-cc-go="overview">Home</button>.
    </p>
    <ul class="team-loc-tree" style="list-style:none;margin:0;padding:0;display:grid;gap:6px">${body}</ul>`;
}
